import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import type { ConversationStatus, LeadStatus } from '@conversia/shared-types';
import { MessageSquareText, ChevronDown, IdCard } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

import { Spinner, SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import * as conversationsApi from '@/api/conversations.api';
import * as asuntosApi from '@/api/asuntos.api';
import * as frentesApi from '@/api/frentes.api';
import { listarCartera } from '@/api/cartera.api';
import { Candado } from '@/components/athena/Candado';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { MedioDelMensaje } from '@/components/athena/MedioDelMensaje';
import { esSoloMarcador } from '@/components/athena/marcadores';
import { TarjetaDeProgreso } from '@/components/cartera/TarjetaDeProgreso';
import { FichaDelContacto } from '@/components/contacts/ContactDetailsPanel';
import { PestanaSinRegistro } from '@/components/pestanas/HojaDePestana';
import * as candado from '@/hooks/candado';
import { useVeRuta } from '@/hooks/useMenu';
import { hora12 } from '@/lib/athena';
import { llaveDelChat } from '@/lib/frentes';
import { claveDe } from '@/lib/pestanas';
import { abrirPestana, usePestanas } from '@/store/pestanas.store';

/**
 * EL CHAT — SOLO LECTURA.
 *
 * Regla dura del mandato: *"No hay caja de escribir."* Esta pantalla no tiene
 * una, y no es un descuido que haya que arreglar después: es la razón por la
 * que existe separada de la bandeja de Conversia vendedor.
 *
 * Tampoco manda a responder a WhatsApp (se quitó el 9-oct): dentro del sistema
 * se contesta en «Chats», y a eso lleva «Abrir en Chats».
 *
 * ═══ ES UNA PESTAÑA (T12) ═══
 *
 * Un chat de «Frentes» se abre como pestaña arriba, con los datos del contacto
 * al lado. La ruta `/chats/:id` sigue valiendo para quien llega por enlace
 * («Ver mensaje» de un asunto): abre la pestaña y deja «Frentes» debajo.
 *
 * ═══ EL MENSAJE RESALTADO ═══
 *
 * Se llega acá desde "Ver mensaje" de un asunto, con `?mensaje=<id>`. Ese
 * mensaje se resalta y se centra en pantalla. Es lo que hace
 * cierto el *"todo se puede rastrear hasta el mensaje exacto"*: sin el
 * resaltado, el dueño cae en un chat de 200 mensajes y tiene que buscar.
 */

/** La ruta `/chats/:id`: no dibuja nada, abre la pestaña del chat sobre «Frentes». */
export default function ChatDeAthena() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const mensaje = params.get('mensaje');

  useEffect(() => {
    if (!id) return;
    abrirPestana({ tipo: 'chat', id, titulo: 'Chat…', datos: mensaje ? { mensaje } : undefined });
    navegar('/chats', { replace: true, state: { conservaLaPestana: true } });
  }, [id, mensaje, navegar]);
  return null;
}

/** El chat, como contenido de su pestaña. `mensaje` es el que se resalta. `onCerrar` cierra la pestaña. */
export function ChatEnPestana({ conversationId: id, mensaje: resaltado, onCerrar }: { conversationId: string; mensaje: string | null; onCerrar: () => void }) {
  const navegar = useNavigate();
  const [abierto, setAbierto] = useState<string | null>(null);
  const [verResumen, setVerResumen] = useState(true);
  // En el teléfono los datos del contacto van plegados; en escritorio, siempre al lado.
  const [verDatos, setVerDatos] = useState(false);
  const refResaltado = useRef<HTMLDivElement | null>(null);

  // ═══ EL CANDADO DE «FRENTES» TAMBIÉN VALE AQUÍ ═══
  // Si el chat está en un frente con llave y el candado está cerrado, la pestaña pide abrirlo
  // y no pide ni pinta un solo mensaje. Lo decide `llaveDelChat`; mientras no se sabe, nada.
  const [desbloqueado, setDesbloqueado] = useState(candado.estaAbierto());
  const frentes = useQuery({ queryKey: ['frentes'], queryFn: frentesApi.getFrentes });
  const chats = useQuery({ queryKey: ['chats', 'llave-de-la-pestana'], queryFn: () => frentesApi.getChats({ limite: 200 }) });
  const llave = frentes.data && chats.data ? llaveDelChat(frentes.data.frentes, chats.data.chats, id) : undefined;
  const seVe = llave === null || (llave !== undefined && desbloqueado);

  const conversacion = useQuery({
    queryKey: ['conversacion', id],
    queryFn: () => conversationsApi.getConversation(id),
    enabled: seVe,
  });

  const mensajes = useQuery({
    queryKey: ['mensajes', id],
    queryFn: () => conversationsApi.listMessages(id, { take: 200 }),
    enabled: seVe,
  });

  const asuntos = useQuery({
    queryKey: ['asuntos', 'chat', id],
    queryFn: () => asuntosApi.getAsuntos({ limite: 200 }),
    enabled: seVe,
  });

  // Los datos del contacto salen de la lista de Clientes, si quien mira tiene esa pantalla.
  const veClientes = useVeRuta('/cartera');
  const cartera = useQuery({ queryKey: ['cartera'], queryFn: listarCartera, enabled: seVe && veClientes });
  const [ahora] = useState(() => new Date());

  // Centrar el mensaje del que salió el asunto. Sin esto, "Ver mensaje" deja
  // al dueño arriba de un chat largo, buscando a mano lo que vino a ver.
  // (Sin mensaje que resaltar el hilo abre arriba, donde esta pantalla pone lo más reciente.)
  useEffect(() => {
    if (!resaltado || !refResaltado.current) return;
    refResaltado.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [resaltado, mensajes.data]);

  const lista = mensajes.data?.items ?? [];
  const deEsteChat = (asuntos.data?.asuntos ?? []).filter((a) => a.conversationId === id);
  const contacto = conversacion.data?.contact;
  const esGrupo = contacto?.isGroup === true;
  const nombre = contacto?.name?.trim() || contacto?.pushName?.trim() || contacto?.phone || 'Chat';
  const fila = contacto ? cartera.data?.filas.find((f) => f.contactId === contacto.id) : undefined;

  // La pestaña toma el nombre de la persona en cuanto se sabe.
  useEffect(() => {
    if (contacto) usePestanas.getState().renombrarPestana(claveDe('chat', id), nombre);
  }, [id, contacto, nombre]);

  if (frentes.isError || chats.isError || conversacion.isError) {
    return <PestanaSinRegistro nombre="Chat" mensaje="No se pudo abrir este chat. Puede que ya no exista; si no, revisa tu conexión y vuelve a abrirlo." onCerrar={onCerrar} />;
  }
  if (llave === undefined) return <SpinnerOverlay label="Abriendo el chat…" />;
  if (!seVe) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Candado nombreDelFrente={llave ?? ''} onAbierto={() => setDesbloqueado(true)} onCancelar={onCerrar} />
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:grid-rows-[auto_minmax(0,1fr)] lg:gap-x-4">
      <div className="flex flex-wrap items-center gap-2 lg:col-start-1 lg:row-start-1">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-semibold text-slate-900 dark:text-slate-50">{nombre}</h1>
          {contacto?.phone && contacto.phone !== nombre && <p className="truncate text-xs text-slate-500 dark:text-slate-400">{contacto.phone}</p>}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="min-h-[44px] lg:hidden"
          aria-expanded={verDatos}
          aria-controls="datos-del-contacto"
          onClick={() => setVerDatos((v) => !v)}
        >
          <IdCard className="h-4 w-4" /> Datos
          <ChevronDown className={cn('h-4 w-4 transition-transform', verDatos && 'rotate-180')} />
        </Button>
        {/* Aquí solo se lee. Se contesta en «Chats», dentro del sistema. */}
        <Button size="sm" className="min-h-[44px] md:min-h-0" onClick={() => navegar(`/conversations/${id}`)}>
          <MessageSquareText className="h-4 w-4" /> Abrir en Chats
        </Button>
      </div>

      {/* ── LO DEL CONTACTO, AL LADO ── */}
      <aside
        id="datos-del-contacto"
        aria-label="Datos del contacto"
        className={cn(
          'min-h-0 shrink-0 space-y-3 overflow-y-auto lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:block lg:max-h-none',
          verDatos ? 'max-h-[50%]' : 'hidden',
        )}
      >
        {/* En su propia caja: la tarjeta mide `h-full` (para la rejilla de «Tarjetas») y aquí se comería el panel. */}
        {fila && (
          <div>
            <TarjetaDeProgreso fila={fila} etapas={cartera.data?.etapas ?? []} ahora={ahora} compacta />
          </div>
        )}

        {/* ── ATHENA EN ESTE CHAT ── */}
        {deEsteChat.length > 0 && (
          <Card>
            <CardBody className="space-y-1">
              <button
                onClick={() => setVerResumen((v) => !v)}
                aria-expanded={verResumen}
                className="flex w-full items-center justify-between text-sm font-semibold text-slate-700 dark:text-slate-200"
              >
                Athena en este chat ({deEsteChat.length})
                <ChevronDown className={cn('h-4 w-4 transition', !verResumen && '-rotate-90')} />
              </button>
              {verResumen &&
                deEsteChat.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setAbierto(a.id)}
                    className="block min-h-[40px] w-full py-1 text-left text-sm text-slate-600 dark:text-slate-300"
                  >
                    · {a.descripcion}
                  </button>
                ))}
            </CardBody>
          </Card>
        )}

        {fila ? (
          <Card>
            <CardBody>
              <FichaDelContacto
                contactId={fila.contactId}
                conversationId={fila.conversacion?.id}
                leadStatus={(fila.conversacion?.temperatura as LeadStatus | null) ?? null}
                conversationStatus={fila.conversacion?.estado as ConversationStatus | undefined}
              />
            </CardBody>
          </Card>
        ) : (
          // Quien no está en la lista de Clientes (un grupo, alguien de la familia): lo que hay, sin huecos.
          conversacion.data &&
          !cartera.isLoading && (
            <Card>
              <CardBody className="space-y-0.5 text-sm">
                <p className="font-semibold text-slate-900 dark:text-slate-100">{nombre}</p>
                {contacto?.phone && contacto.phone !== nombre && <p className="text-slate-600 dark:text-slate-300">{contacto.phone}</p>}
                <p className="text-xs text-slate-500 dark:text-slate-400">{esGrupo ? 'Es un grupo.' : 'No está en tu lista de clientes.'}</p>
              </CardBody>
            </Card>
          )
        )}
      </aside>

      <div
        role="region"
        aria-label="Mensajes"
        tabIndex={0}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border border-surface-border bg-white p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 lg:col-start-1 lg:row-start-2 dark:border-slate-800 dark:bg-slate-900"
      >
        {mensajes.isPending ? (
          <Spinner />
        ) : lista.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">No hay mensajes.</p>
        ) : (
          [...lista].reverse().map((m) => {
            const mio = m.role !== 'CLIENT';
            const esEse = resaltado === m.id;
            return (
              <div
                key={m.id}
                ref={esEse ? refResaltado : undefined}
                className={cn('flex', mio ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[80%] space-y-1 rounded-2xl px-3 py-2 text-sm transition',
                    mio
                      ? 'bg-brand-600 text-white'
                      : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                    esEse && 'ring-2 ring-amber-400',
                  )}
                >
                  {/* EN GRUPOS, QUIEN HABLA.
                      Sin esto, un grupo de 15 es una pila de burbujas iguales
                      y no se sabe quien dijo que — que es justo lo que uno
                      entra a ver. Solo en los ajenos: los propios ya se
                      distinguen por el lado y el color. */}
                  {!mio && esGrupo && m.senderName && (
                    <p className="text-[11px] font-semibold text-brand-600 dark:text-brand-300">
                      {m.senderName}
                    </p>
                  )}

                  <MedioDelMensaje mensaje={m} mio={mio} />

                  {/* El texto SOLO si dice algo. Con una foto sin epigrafe, el
                      adaptador deja "[Imagen]" como contenido: pintarlo debajo
                      de la imagen ya visible es ruido. */}
                  {m.content && !esSoloMarcador(m.content) && (
                    <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  )}

                  <p className={cn('text-[11px]', mio ? 'text-white/70' : 'text-slate-400')}>
                    {hora12(m.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
